// 4X Legendary Chess — audio engine.
// • Sound effects: ONE sprite file (/audio/sfx_sprite.mp3) played through Web Audio.
// • Music: one looping background track at a time (HTMLAudioElement).
// • Mute: obeys the existing "Sound FX" toggle (localStorage "chess_sound_off" === "1").
// • Volumes: localStorage "4xlc_sfx_volume" and "4xlc_music_volume" (0..1).
const SPRITE_URL = "/audio/sfx_sprite.mp3";
// name: [startSeconds, durationSeconds] inside sfx_sprite.mp3
const SPRITE = {
  move_1: [0.5, 0.37],
  move_2: [1.37, 0.341],
  move_3: [2.211, 0.341],
  move_4: [3.051, 0.341],
  move_5: [3.892, 0.341],
  capture: [4.733, 0.75],
  check: [5.983, 2.55],
  match_start: [9.033, 5.0],
  victory: [14.533, 5.6],
  defeat: [20.633, 2.3],
  click: [23.433, 0.048],
  coin: [23.981, 1.05],
  card_flip: [25.531, 0.8],
  rope_fuse_loop: [26.831, 5.208],
  clock_tick_loop: [32.539, 4.0],
};
const MOVE_VARIANTS = ["move_1", "move_2", "move_3", "move_4", "move_5"];
const MUSIC = {
  menu: "/audio/music/menu_castle_loop.mp3",
  "iron-kingdom": "/audio/music/theme_iron_kingdom.mp3",
  dragonborn: "/audio/music/theme_dragonborn.mp3",
  "elf-court": "/audio/music/theme_elf_court.mp3",
  "celestial-host": "/audio/music/theme_celestial_host.mp3",
  relaxing: "/audio/music/station_relaxing.mp3",
  jogging: "/audio/music/station_jogging.mp3",
  classical: "/audio/music/station_classical.mp3",
  uptempo: "/audio/music/station_uptempo.mp3",
};
// Player-selectable music stations. "faction" = castle loop on menus,
// the player's faction theme during games.
export const MUSIC_STATIONS = [
  { value: "faction", label: "Faction Themes" },
  { value: "relaxing", label: "Relaxing" },
  { value: "jogging", label: "Jogging" },
  { value: "classical", label: "Classical" },
  { value: "uptempo", label: "Uptempo" },
  { value: "mine", label: "My Music" },
  { value: "radio", label: "Live Radio" },
];
const LS_SFX = "4xlc_sfx_volume";
const LS_MUSIC = "4xlc_music_volume";
const LS_MUSIC_ON = "4xlc_music_on";
const LS_STATION = "4xlc_music_station";
function readVol(key, dflt) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return dflt;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : dflt;
  } catch {
    return dflt;
  }
}
function saveVol(key, v) {
  try { window.localStorage.setItem(key, String(v)); } catch { /* ignore */ }
}
function sfxMuted() {
  try { return window.localStorage.getItem("chess_sound_off") === "1"; } catch { return false; }
}
let sfxVolume = typeof window !== "undefined" ? readVol(LS_SFX, 0.8) : 0.8;
let musicVolume = typeof window !== "undefined" ? readVol(LS_MUSIC, 0.35) : 0.35;
// ---------------- Sound effects ----------------
let ctx = null;
let buffer = null;
let loading = null;
const loops = {};
function getCtx() {
  if (!ctx) {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    try { ctx = new C(); } catch { return null; }
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}
function loadSprite() {
  if (buffer) return Promise.resolve(buffer);
  if (loading) return loading;
  const c = getCtx();
  if (!c) return Promise.resolve(null);
  loading = fetch(SPRITE_URL)
    .then((r) => r.arrayBuffer())
    .then((ab) => new Promise((resolve, reject) => c.decodeAudioData(ab, resolve, reject)))
    .then((b) => { buffer = b; return b; })
    .catch(() => { loading = null; return null; });
  return loading;
}
export function preloadSfx() {
  loadSprite();
}
export function playSfx(name) {
  if (sfxMuted() || sfxVolume <= 0) return;
  const key = name === "move"
    ? MOVE_VARIANTS[Math.floor(Math.random() * MOVE_VARIANTS.length)]
    : name;
  const seg = SPRITE[key];
  if (!seg) return;
  loadSprite().then((b) => {
    if (!b || !ctx) return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = b;
      const g = ctx.createGain();
      g.gain.value = sfxVolume;
      src.connect(g);
      g.connect(ctx.destination);
      // 20 ms lead-in / 40 ms tail of silence keeps every clip whole.
      src.start(0, Math.max(0, seg[0] - 0.02), seg[1] + 0.06);
    } catch { /* no audio */ }
  });
}
export function startLoop(name) {
  if (loops[name]) return;
  const seg = SPRITE[name];
  if (!seg) return;
  if (sfxMuted() || sfxVolume <= 0) return;
  loops[name] = "pending";
  loadSprite().then((b) => {
    if (loops[name] !== "pending") return;
    if (!b || !ctx) { delete loops[name]; return; }
    try {
      const src = ctx.createBufferSource();
      src.buffer = b;
      src.loop = true;
      src.loopStart = seg[0];
      src.loopEnd = seg[0] + seg[1];
      const g = ctx.createGain();
      g.gain.value = sfxVolume;
      src.connect(g);
      g.connect(ctx.destination);
      src.start(0, seg[0]);
      loops[name] = { src, g };
    } catch { delete loops[name]; }
  });
}
export function stopLoop(name) {
  const l = loops[name];
  delete loops[name];
  if (l && l !== "pending") {
    try { l.src.stop(); } catch { /* already stopped */ }
  }
}
export function stopAllLoops() {
  Object.keys(loops).forEach(stopLoop);
}
export function getSfxVolume() { return sfxVolume; }
export function setSfxVolume(v) {
  sfxVolume = Math.min(1, Math.max(0, Number(v) || 0));
  saveVol(LS_SFX, sfxVolume);
  Object.values(loops).forEach((l) => { if (l && l !== "pending") l.g.gain.value = sfxVolume; });
  if (sfxVolume <= 0) stopAllLoops();
}
// ---------------- Music ----------------
function readStr(key, dflt) {
  try { const v = window.localStorage.getItem(key); return v === null ? dflt : v; } catch { return dflt; }
}
function saveStr(key, v) {
  try { window.localStorage.setItem(key, v); } catch { /* ignore */ }
}
let musicOn = typeof window !== "undefined" ? readStr(LS_MUSIC_ON, "1") !== "0" : true;
let station = typeof window !== "undefined" ? readStr(LS_STATION, "faction") : "faction";
if (!MUSIC_STATIONS.some((s) => s.value === station)) station = "faction";
let musicEl = null;
let currentTrack = null;
// Every music element created so far. Before a new one starts, all others are
// stopped and released, so two tracks can never play on top of each other.
const liveEls = new Set();
function silenceAll() {
  liveEls.forEach((el) => {
    try { el.pause(); } catch { /* ignore */ }
    try { el.removeAttribute("src"); el.load(); } catch { /* ignore */ }
  });
  liveEls.clear();
}
function makeMusicEl(src) {
  silenceAll();
  const el = new Audio(src);
  liveEls.add(el);
  return el;
}
let currentContext = "menu"; // "menu" or "game"
let unlockBound = false;
// "My Music": songs the player picks from their own device. They play only in
// this browser (object URLs) — nothing is uploaded or stored on the server.
let myTracks = []; // [{ name, url }]
let myIndex = 0;
// "Live Radio": a real AM/FM station's public internet stream (Oct 2026).
const LS_RADIO = "4xlc_radio_station";
const LS_RADIO_FAVS = "4xlc_radio_favs";
function readJson(key, dflt) {
  try { const v = window.localStorage.getItem(key); return v ? JSON.parse(v) : dflt; } catch { return dflt; }
}
function saveJson(key, v) {
  try { window.localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
}
function cleanStation(s) {
  if (!s || typeof s.url !== "string" || !s.url.startsWith("https://") || !s.id) return null;
  return {
    id: String(s.id), name: String(s.name || "Radio").slice(0, 80), url: s.url,
    band: s.band === "AM" || s.band === "FM" ? s.band : null, city: String(s.city || "").slice(0, 40),
  };
}
let radioStation = typeof window !== "undefined" ? cleanStation(readJson(LS_RADIO, null)) : null;
let radioError = false;
const listeners = new Set();
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}
// UI components subscribe so the Settings page and the floating music
// button always show the same state.
export function subscribeMusic(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function resolveTrack() {
  if (station === "mine") return myTracks.length ? `mine:${myIndex}` : "menu";
  if (station === "radio") return radioStation ? `radio:${radioStation.id}` : "silent";
  if (station !== "faction") return station;
  if (currentContext !== "game") return "menu";
  let f = "iron-kingdom";
  try { f = window.localStorage.getItem("chess_faction") || f; } catch { /* default */ }
  return MUSIC[f] ? f : "menu";
}
function tryPlay() {
  if (!musicEl || !musicOn || musicVolume <= 0) return;
  const p = musicEl.play();
  if (p && p.catch) {
    p.catch(() => {
      // Browsers block audio until the first tap/key — start on that gesture.
      if (unlockBound) return;
      unlockBound = true;
      const go = () => {
        unlockBound = false;
        window.removeEventListener("pointerdown", go);
        window.removeEventListener("keydown", go);
        getCtx();
        if (musicEl && musicOn && musicVolume > 0) musicEl.play().catch(() => {});
      };
      window.addEventListener("pointerdown", go);
      window.addEventListener("keydown", go);
    });
  }
}
function applyMusic() {
  if (!musicOn) {
    if (musicEl) { try { musicEl.pause(); } catch { /* ignore */ } }
    return;
  }
  const key = resolveTrack();
  if (key === "silent") {
    // Live Radio with no station picked yet: play nothing (Oct 2026).
    currentTrack = key;
    silenceAll();
    musicEl = null;
    return;
  }
  if (key === currentTrack && musicEl) {
    musicEl.volume = musicVolume;
    if (musicEl.paused) tryPlay();
    return;
  }
  currentTrack = key;
  const mine = key.startsWith("mine:") ? myTracks[myIndex] : null;
  const radio = key.startsWith("radio:") ? radioStation : null;
  // makeMusicEl stops EVERY other music element first — only one can ever play.
  const el = makeMusicEl(radio ? radio.url : mine ? mine.url : MUSIC[key]);
  musicEl = el;
  el.loop = !radio && (!mine || myTracks.length === 1);
  if (radio) {
    radioError = false;
    el.addEventListener("error", () => { if (el === musicEl) { radioError = true; notify(); } });
    el.addEventListener("playing", () => { if (el === musicEl && radioError) { radioError = false; notify(); } });
  }
  if (mine && myTracks.length > 1) {
    el.addEventListener("ended", () => {
      if (el !== musicEl) return;
      myIndex = (myIndex + 1) % myTracks.length;
      applyMusic();
      notify();
    });
  }
  el.preload = "auto";
  el.volume = musicVolume;
  el.addEventListener("error", () => {
    // Missing file (e.g. a theme not uploaded yet) → fall back to the menu loop.
    // Errors from an element that is no longer the current one are ignored, so a
    // late error can never start a second track on top of the one playing.
    if (el !== musicEl || radio || mine || key === "menu") return;
    currentTrack = "menu";
    const fallback = makeMusicEl(MUSIC.menu);
    fallback.loop = true;
    fallback.volume = musicVolume;
    musicEl = fallback;
    tryPlay();
  });
  tryPlay();
}
// context: "menu" (any non-game page) or "game" (a game is on screen).
export function playMusic(context) {
  currentContext = context === "game" ? "game" : "menu";
  applyMusic();
}
export function stopMusic() {
  if (musicEl) { try { musicEl.pause(); } catch { /* ignore */ } }
}
export function getMusicOn() { return musicOn; }
export function setMusicOn(on) {
  musicOn = !!on;
  saveStr(LS_MUSIC_ON, musicOn ? "1" : "0");
  applyMusic();
  notify();
}
export function getStation() { return station; }
export function setStation(value) {
  if (!MUSIC_STATIONS.some((s) => s.value === value)) return;
  station = value;
  saveStr(LS_STATION, station);
  if (!musicOn) { musicOn = true; saveStr(LS_MUSIC_ON, "1"); }
  applyMusic();
  notify();
}
export function getMusicVolume() { return musicVolume; }
export function setMusicVolume(v) {
  musicVolume = Math.min(1, Math.max(0, Number(v) || 0));
  saveVol(LS_MUSIC, musicVolume);
  if (musicEl) {
    musicEl.volume = musicVolume;
    if (musicVolume <= 0) musicEl.pause();
    else if (musicOn && musicEl.paused) tryPlay();
  }
  notify();
}
export function resumeMusic() {
  applyMusic();
}
const AUDIO_EXT = /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|webm)$/i;
export function setMyMusicFiles(files) {
  const list = Array.from(files || []).filter(
    (f) => f && ((f.type && f.type.startsWith("audio/")) || AUDIO_EXT.test(f.name || "")),
  );
  if (!list.length) return 0;
  myTracks.forEach((t) => { try { URL.revokeObjectURL(t.url); } catch { /* ignore */ } });
  myTracks = list.map((f) => ({ name: (f.name || "Song").replace(/\.[^.]+$/, ""), url: URL.createObjectURL(f) }));
  myIndex = 0;
  currentTrack = null;
  station = "mine";
  saveStr(LS_STATION, station);
  musicOn = true;
  saveStr(LS_MUSIC_ON, "1");
  applyMusic();
  notify();
  return myTracks.length;
}
export function getMyMusic() {
  return { count: myTracks.length, current: myTracks.length ? myTracks[myIndex].name : null };
}
export function nextMyTrack() {
  if (myTracks.length < 2) return;
  myIndex = (myIndex + 1) % myTracks.length;
  applyMusic();
  notify();
}
// ---------------- Menu click sound ----------------
let clickInstalled = false;
export function installClickSound() {
  if (clickInstalled || typeof document === "undefined") return;
  clickInstalled = true;
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target && e.target.closest ? e.target.closest("button, a[href], [role='button']") : null;
      if (!el) return;
      if (el.closest("[data-no-click-sound]")) return;
      playSfx("click");
    },
    true,
  );
}

// ---------------- Live Radio (Oct 2026) ----------------
export function getRadioStation() { return radioStation; }
export function getRadioError() { return radioError; }
export function playRadioStation(s) {
  const clean = cleanStation(s);
  if (!clean) return;
  radioStation = clean;
  saveJson(LS_RADIO, clean);
  currentTrack = null;
  station = "radio";
  saveStr(LS_STATION, station);
  musicOn = true;
  saveStr(LS_MUSIC_ON, "1");
  radioError = false;
  applyMusic();
  notify();
}
export function getRadioFavorites() {
  const list = readJson(LS_RADIO_FAVS, []);
  return Array.isArray(list) ? list.map(cleanStation).filter(Boolean) : [];
}
export function toggleRadioFavorite(s) {
  const clean = cleanStation(s);
  if (!clean) return;
  const list = getRadioFavorites();
  const next = list.some((f) => f.id === clean.id)
    ? list.filter((f) => f.id !== clean.id)
    : [clean, ...list].slice(0, 50);
  saveJson(LS_RADIO_FAVS, next);
  notify();
}
